// Minimal authentication provider - delegates to Supabase
import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import type { User, AuthContextType, UserTier } from './types';
import {
  // signInWithGoogle, // OAuth providers temporarily disabled
  // signInWithApple,  // OAuth providers temporarily disabled
  signInWithEmail,
  signUpWithEmail,
  signInWithPhone,
  verifyPhone,
  signOut,
  onAuthStateChange
} from './supabaseAuthService';
import { updateUserTier } from '../data/dataService';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Auth methods - just pass through to service
  
  // OAuth handlers - temporarily disabled
  // TODO: Re-enable when OAuth providers are properly configured
  /*
  const handleSignInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const handleSignInWithApple = useCallback(async () => {
    setLoading(true);
    try {
      await signInWithApple();
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);
  */

  const handleSignInWithEmail = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const user = await signInWithEmail(email, password);
      setUser(user);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const handleSignUpWithEmail = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const user = await signUpWithEmail(email, password);
      setUser(user);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const handleSignInWithPhone = useCallback(async (phoneNumber: string) => {
    setLoading(true);
    try {
      await signInWithPhone(phoneNumber);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const handleVerifyPhone = useCallback(async (phoneNumber: string, otp: string) => {
    setLoading(true);
    try {
      const user = await verifyPhone(phoneNumber, otp);
      setUser(user);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(null);
  }, []);

  const handleUpdateUserTier = useCallback(async (tier: UserTier) => {
    if (!user) return;
    
    const result = await updateUserTier(user.uid, tier);
    if (result.success) {
      setUser({ ...user, tier });
    } else {
      throw new Error(result.error);
    }
  }, [user]);

  const handleRefreshUser = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Import getCurrentUser to get fresh data from the database
      const { getCurrentUser } = await import('./supabaseAuthService');
      const freshUser = await getCurrentUser();
      
      if (freshUser) {
        setUser(freshUser);
      }
    } catch {
      // Silently handle refresh errors in production
    } finally {
      setLoading(false);
    }
  }, [user]);

  const contextValue: AuthContextType = {
    user,
    loading,
    // OAuth methods - temporarily disabled, provide placeholders
    signInWithGoogle: async () => { 
      throw new Error('Google sign-in is temporarily disabled'); 
    },
    signInWithApple: async () => { 
      throw new Error('Apple sign-in is temporarily disabled'); 
    },
    signInWithEmail: handleSignInWithEmail,
    signUpWithEmail: handleSignUpWithEmail,
    signInWithPhone: handleSignInWithPhone,
    verifyPhone: handleVerifyPhone,
    signOut: handleSignOut,
    updateUserTier: handleUpdateUserTier,
    refreshUser: handleRefreshUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};